/**
 * Gerador procedural do rio — §6 da especificação.
 *
 * A propriedade que governa todo o desenho: um chunk é uma FUNÇÃO PURA de
 * `(worldSeed, index, safe)`. Nada de estado acumulado entre chunks — é isso
 * que permite gerar, descartar e regenerar chunks fora de ordem (essencial no
 * respawn, que rebobina o mundo) e é o que faz host, guest e revalidação de
 * replay enxergarem o mesmo rio sem trafegar um byte de terreno.
 *
 * A DIFICULDADE também é pura: a seção que governa largura, orçamento, ilhas
 * e jatos é a POSIÇÃO do chunk (`sectionOfChunk`), não o contador de pontes
 * destruídas do HUD — dois clientes geram o mesmo mundo mesmo que um deles
 * gere o chunk antes de a ponte anterior cair.
 *
 * A suavidade (≤ 1 tile de variação por linha por margem, §6.2) é garantida
 * por construção: valores de malha limitados + espaçamento largo ⇒ derivada
 * < 1 tile/linha; onde um alvo precisa ser forçado (reta segura, largura de
 * ponte), usa-se MARCHA de passo limitado, nunca blend analítico. O teste de
 * 1.000 seeds prova tudo isso, fronteiras de chunk incluídas.
 *
 * Aritmética: inteiros Q8 e divisões exatas. Nenhuma transcendental — o lint
 * de pureza vigia.
 */
import { BRIDGE, DIFFICULTY, ENTITY, GEN, ISLAND, SCREEN, SPAWN, WORLD, isBridgeChunk, perTick, sectionOfChunk, } from '../config.js';
import { clamp, fp } from '../fixed.js';
import { mixSeed, RngService, chunkSeed } from '../rng.js';
const SALT_WIDTH = 0x57494454; // "WIDT"
const SALT_CENTER = 0x43454e54; // "CENT"
const SALT_ENTITIES = 0x454e5453; // "ENTS"
const SALT_ISLAND = 0x49534c44; // "ISLD"
const TILE_Q8 = 256;
/** Valor da malha em `k`: função pura de (worldSeed, salt, k). */
function latticeValue(worldSeed, salt, k, min, max) {
    return new RngService(mixSeed(mixSeed(worldSeed, salt), k)).intBetween(min, max);
}
/**
 * Faixa de largura da malha no ponto `k` — a curva da §10 em inteiros.
 *
 * Média-alvo `max(6, 10 − 0,4·s)` tiles: o rio começa largo (média 10) e
 * estreita 0,4 tile por seção até o piso 6. A seção do ponto de malha é a da
 * LINHA do ponto (função pura de k), então a transição entre seções entra
 * suavemente pela própria interpolação.
 */
function widthRangeAt(k, playerCount) {
    const line = k * GEN.LATTICE_LINES;
    const section = Math.min(DIFFICULTY.CAP_SECTION, (line / (BRIDGE.EVERY_CHUNKS * WORLD.CHUNK_LINES)) | 0);
    const meanX10 = Math.max(60, 100 - 4 * section);
    // Largura mínima cresce com o time (§10): 4 aviões precisam de espaço —
    // afunilamento com muita gente é injustiça, não desafio.
    const minWidth = DIFFICULTY.MIN_WIDTH_BY_PLAYERS[playerCount - 1] ?? WORLD.RIVER_MIN_TILES;
    const lo = Math.max(minWidth, Math.round((2 * meanX10 - 120) / 10));
    const hi = clamp(Math.round((2 * meanX10 - 10 * lo) / 10), lo + 2, WORLD.RIVER_MAX_TILES);
    return [lo, hi];
}
/** Perfil natural do rio na linha absoluta `y`. Interpolação linear (§6.2). */
function naturalProfile(worldSeed, y, playerCount) {
    const L = GEN.LATTICE_LINES;
    const k = (y / L) | 0;
    const t = y - k * L;
    const [lo0, hi0] = widthRangeAt(k, playerCount);
    const [lo1, hi1] = widthRangeAt(k + 1, playerCount);
    const w0 = latticeValue(worldSeed, SALT_WIDTH, k, lo0, hi0) * TILE_Q8;
    const w1 = latticeValue(worldSeed, SALT_WIDTH, k + 1, lo1, hi1) * TILE_Q8;
    const c0 = latticeValue(worldSeed, SALT_CENTER, k, GEN.CENTER_MIN_TILE, GEN.CENTER_MAX_TILE) * TILE_Q8;
    const c1 = latticeValue(worldSeed, SALT_CENTER, k + 1, GEN.CENTER_MIN_TILE, GEN.CENTER_MAX_TILE) * TILE_Q8;
    // (b − a) é múltiplo de 256 e t ≤ 15: o produto dividido por 16 é inteiro
    // exato — nenhum arredondamento entra no caminho do estado.
    const widthQ8 = w0 + ((w1 - w0) * t) / L;
    const centerRaw = c0 + ((c1 - c0) * t) / L;
    const half = widthQ8 / 2;
    const lo = GEN.EDGE_MARGIN_TILES * TILE_Q8 + half;
    const hi = (SCREEN.GRID_W - GEN.EDGE_MARGIN_TILES) * TILE_Q8 - half;
    return { widthQ8, centerQ8: clamp(centerRaw, lo, hi) };
}
/** Rasteriza um perfil contínuo em bordas inteiras de tile. */
function rasterizeTiles(profile) {
    const half = profile.widthQ8 / 2;
    return [
        Math.round((profile.centerQ8 - half) / TILE_Q8),
        Math.round((profile.centerQ8 + half) / TILE_Q8),
    ];
}
/** Um passo de marcha: move `from` na direção de `to`, no máximo 1 tile. */
function marchStep(from, to) {
    return from + clamp(to - from, -1, 1);
}
/**
 * Força uma banda [bandStart, bandEnd) com bordas fixas, marchando das pontas
 * naturais do chunk até a banda com passo ≤ 1 tile/linha. As linhas 0 e 23
 * permanecem naturais: a fronteira com qualquer vizinho continua válida sem
 * que o vizinho saiba da banda. (A marcha, e não um blend analítico, é o que
 * garante o limite de inclinação POR CONSTRUÇÃO: blend persegue alvo móvel e
 * soma inclinações além do limite.)
 */
function forceBand(leftTiles, rightTiles, bandLeft, bandRight, bandStart, bandEnd) {
    for (let i = bandStart; i < bandEnd; i++) {
        leftTiles[i] = bandLeft;
        rightTiles[i] = bandRight;
    }
    for (let i = 1; i < bandStart; i++) {
        leftTiles[i] = marchStep(leftTiles[i - 1], bandLeft);
        rightTiles[i] = marchStep(rightTiles[i - 1], bandRight);
    }
    for (let i = WORLD.CHUNK_LINES - 2; i >= bandEnd; i--) {
        leftTiles[i] = marchStep(leftTiles[i + 1], bandLeft);
        rightTiles[i] = marchStep(rightTiles[i + 1], bandRight);
    }
}
/** Bordas da banda reta de um chunk seguro, em tiles (ver M1). */
function safeBandTiles(entry, exit) {
    let left = Math.round((entry[0] + exit[0]) / 2);
    let right = Math.round((entry[1] + exit[1]) / 2);
    while (right - left < WORLD.SAFE_STRAIGHT_MIN_TILES) {
        if (left > GEN.EDGE_MARGIN_TILES)
            left--;
        if (right - left < WORLD.SAFE_STRAIGHT_MIN_TILES && right < SCREEN.GRID_W - GEN.EDGE_MARGIN_TILES) {
            right++;
        }
    }
    if (left < GEN.EDGE_MARGIN_TILES) {
        right += GEN.EDGE_MARGIN_TILES - left;
        left = GEN.EDGE_MARGIN_TILES;
    }
    const maxRight = SCREEN.GRID_W - GEN.EDGE_MARGIN_TILES;
    if (right > maxRight) {
        left -= right - maxRight;
        right = maxRight;
    }
    return [left, right];
}
/**
 * Planeja a ilha do chunk (§6.2/§6.3): probabilidade pela seção, sempre
 * inteira dentro do chunk (reconvergência garantida por construção — a regra
 * "sem beco" nunca depende do vizinho), canais ≥ 3 tiles dos dois lados em
 * TODAS as linhas.
 */
function planIsland(rng, leftTiles, rightTiles, section) {
    const probNum = Math.min(DIFFICULTY.ISLAND_PROB_MAX_NUM, DIFFICULTY.ISLAND_PROB_BASE_NUM + 5 * section);
    if (!rng.chance(probNum, DIFFICULTY.ISLAND_PROB_DEN))
        return null;
    const lengthLines = rng.intBetween(ISLAND.LENGTH_MIN_LINES, ISLAND.LENGTH_MAX_LINES);
    if (lengthLines > WORLD.CHUNK_LINES - 2)
        return null;
    const startLine = rng.intBetween(1, WORLD.CHUNK_LINES - 1 - lengthLines);
    // Espaço navegável mínimo ao longo das linhas da ilha decide a largura.
    let maxLeft = 0;
    let minRight = SCREEN.GRID_W;
    for (let i = startLine; i < startLine + lengthLines; i++) {
        maxLeft = Math.max(maxLeft, leftTiles[i]);
        minRight = Math.min(minRight, rightTiles[i]);
    }
    const room = minRight - maxLeft - 2 * ISLAND.CHANNEL_MIN_TILES;
    if (room < ISLAND.WIDTH_MIN_TILES)
        return null;
    const widthTiles = Math.min(ISLAND.WIDTH_MAX_TILES, room, rng.intBetween(1, ISLAND.WIDTH_MAX_TILES));
    const loTile = maxLeft + ISLAND.CHANNEL_MIN_TILES;
    const hiTile = minRight - ISLAND.CHANNEL_MIN_TILES - widthTiles;
    if (hiTile < loTile)
        return null;
    return { startLine, lengthLines, leftTile: rng.intBetween(loTile, hiTile), widthTiles };
}
/** Rasteriza a ilha com pontas afuniladas (≤ 1 tile de variação por linha). */
function rasterizeIsland(plan, islandLeftPx, islandRightPx) {
    for (let j = 0; j < plan.lengthLines; j++) {
        const i = plan.startLine + j;
        // Largura triangular com platô: cresce 1/linha, segura, decresce 1/linha.
        const w = Math.min(plan.widthTiles, j + 1, plan.lengthLines - j);
        const inset = plan.widthTiles - w;
        const leftInset = (inset + 1) >> 1;
        const left = plan.leftTile + leftInset;
        islandLeftPx[i] = left * SCREEN.TILE;
        islandRightPx[i] = (left + w) * SCREEN.TILE;
    }
}
// ─── Entidades ────────────────────────────────────────────────────────────────
function entityWidth(type) {
    switch (type) {
        case 'ship':
            return ENTITY.SHIP.W;
        case 'heli':
            return ENTITY.HELI.W;
        case 'depot':
            return ENTITY.DEPOT.W;
        case 'jet':
            return ENTITY.JET.W;
        case 'bridge':
            return SCREEN.WIDTH;
    }
}
/** Orçamento de entidades por chunk na seção (§10), sem o multiplicador co-op. */
export function entityBudget(section, playerCount = 1) {
    const grown = Math.round((DIFFICULTY.ENTITY_BUDGET_BASE *
        (DIFFICULTY.ENTITY_BUDGET_GROWTH_DEN + DIFFICULTY.ENTITY_BUDGET_GROWTH_NUM * section)) /
        DIFFICULTY.ENTITY_BUDGET_GROWTH_DEN);
    // Multiplicador sublinear por jogador (§10): pressão sobe, sem virar
    // parede de balas.
    const pct = DIFFICULTY.BUDGET_BY_PLAYERS_PCT[playerCount - 1] ?? 100;
    return Math.min(DIFFICULTY.ENTITY_BUDGET_MAX, Math.round((grown * pct) / 100));
}
/**
 * Regra 3 da §6.3: entidade estática precisa de ≥ 4 linhas de reação após um
 * estreitamento ≥ 2 tiles. Verificação intra-chunk (a fronteira fica com o
 * validador de 1.000 seeds, que nunca a viu violada com a malha atual).
 */
function violatesReactionRule(ctx, line) {
    const widthAt = (i) => ctx.rightPx[i] - ctx.leftPx[i];
    for (let back = 1; back <= SPAWN.REACTION_LINES; back++) {
        const previous = line - back;
        if (previous < 0)
            break;
        if (widthAt(previous) - widthAt(line) >= SPAWN.REACTION_NARROWING_TILES * SCREEN.TILE)
            return true;
    }
    return false;
}
/** Sorteia posição X dentro de UM canal navegável da linha (ilha respeitada). */
function pickX(rng, ctx, line, type) {
    const width = entityWidth(type);
    const margin = SPAWN.X_MARGIN_PX + ((width / 2) | 0);
    const islandLeft = ctx.islandLeftPx[line];
    const islandRight = ctx.islandRightPx[line];
    const channels = [];
    if (islandRight > islandLeft) {
        channels.push([ctx.leftPx[line], islandLeft]);
        channels.push([islandRight, ctx.rightPx[line]]);
    }
    else {
        channels.push([ctx.leftPx[line], ctx.rightPx[line]]);
    }
    const viable = channels.filter(([lo, hi]) => hi - lo >= 2 * margin);
    if (viable.length === 0)
        return null;
    // Canal sorteado com peso pela largura: o canal largo recebe mais tráfego.
    let choice = viable[0];
    if (viable.length === 2) {
        const w0 = viable[0][1] - viable[0][0];
        const w1 = viable[1][1] - viable[1][0];
        choice = rng.chance(w0, w0 + w1) ? viable[0] : viable[1];
    }
    return rng.intBetween(choice[0] + margin, choice[1] - margin);
}
/**
 * Colocação de entidades (§6.2 passo 5): orçamento pela seção, sempre dentro
 * de um canal navegável, espaçamento vertical mínimo, regra de reação para
 * estáticos, jatos a partir da seção 3 e ponte nos chunks posicionais.
 *
 * O PRIMEIRO slot de todo chunk é um DEPÓSITO: com um depósito a cada 24
 * linhas, o pior intervalo entre reabastecimentos fica ordens de grandeza
 * abaixo do teto de viabilidade (regra 1 da §6.3) — por CONSTRUÇÃO, sem
 * estado entre chunks.
 */
function placeEntities(worldSeed, index, ctx, section, hasBridge, playerCount) {
    const rng = new RngService(mixSeed(chunkSeed(worldSeed, index), SALT_ENTITIES));
    const spawns = [];
    const usedLines = [];
    const baseLine = index * WORLD.CHUNK_LINES;
    // A ponte primeiro: barreira na linha fixa, o resto respeita a distância.
    if (hasBridge) {
        const i = BRIDGE.LINE;
        const center = Math.round((ctx.leftPx[i] + ctx.rightPx[i]) / 2);
        spawns.push({ slot: 0, type: 'bridge', line: baseLine + i, xQ8: fp(center), vxQ8: 0, dir: 1 });
        usedLines.push(i);
    }
    // Com ponte, o miolo e o pós-ponte ficam livres: entidades só bem antes
    // dela (respiro de travessia, §10) — o jogador chega na ponte limpo.
    const lineMax = hasBridge ? BRIDGE.LINE - SPAWN.REACTION_LINES : SPAWN.LINE_MAX;
    const budget = entityBudget(section, playerCount);
    const heliBonus = Math.min(DIFFICULTY.HELI_SPEED_MAX_BONUS, DIFFICULTY.HELI_SPEED_PER_SECTION * section);
    // Densidade de depósitos por time (§10): a fração acima de 100% vira a
    // chance de um SEGUNDO depósito garantido no chunk.
    const depotPct = DIFFICULTY.DEPOT_DENSITY_BY_PLAYERS_PCT[playerCount - 1] ?? 100;
    const wantSecondDepot = depotPct > 100 && rng.chance(depotPct - 100, 100);
    for (let slot = 0; slot < budget; slot++) {
        const depotsSoFar = spawns.filter((sp) => sp.type === 'depot').length;
        const type = depotsSoFar === 0 || (wantSecondDepot && depotsSoFar === 1 && slot >= 2)
            ? 'depot'
            : rng.chance(SPAWN.SHIP_WEIGHT, 100)
                ? 'ship'
                : 'heli';
        let chosen = -1;
        let x = null;
        let vxPxS = 0;
        for (let attempt = 0; attempt < SPAWN.TRIES_PER_SLOT; attempt++) {
            const line = rng.intBetween(SPAWN.LINE_MIN, lineMax);
            let clash = false;
            for (const used of usedLines) {
                if (Math.abs(line - used) < WORLD.MIN_ENTITY_GAP_LINES) {
                    clash = true;
                    break;
                }
            }
            if (clash)
                continue;
            vxPxS =
                type === 'ship'
                    ? rng.intBetween(ENTITY.SHIP.DRIFT_MIN, ENTITY.SHIP.DRIFT_MAX)
                    : type === 'heli'
                        ? rng.intBetween(ENTITY.HELI.PATROL_MIN, ENTITY.HELI.PATROL_MAX) + heliBonus
                        : 0;
            // Estático parado depois de um funil é morte sem reação (regra 3).
            const isStatic = type === 'depot' || (type === 'ship' && vxPxS === 0);
            if (isStatic && violatesReactionRule(ctx, line))
                continue;
            x = pickX(rng, ctx, line, type);
            if (x === null)
                continue;
            chosen = line;
            break;
        }
        if (chosen < 0 || x === null)
            continue;
        usedLines.push(chosen);
        spawns.push({
            slot: spawns.length,
            type,
            line: baseLine + chosen,
            xQ8: fp(x),
            vxQ8: perTick(vxPxS),
            dir: rng.chance(1, 2) ? 1 : -1,
        });
    }
    // Jatos (§8/§10): a partir da seção 3, frequência 0,3 + 0,1·s por chunk.
    if (section >= DIFFICULTY.JET_FROM_SECTION) {
        const freqX10 = Math.min(DIFFICULTY.JET_FREQ_MAX_NUM, DIFFICULTY.JET_FREQ_BASE_NUM + DIFFICULTY.JET_FREQ_GROWTH_NUM * section);
        let jets = (freqX10 / 10) | 0;
        if (rng.chance(freqX10 % 10, 10))
            jets++;
        for (let j = 0; j < jets; j++) {
            let line = -1;
            for (let attempt = 0; attempt < SPAWN.TRIES_PER_SLOT; attempt++) {
                const candidate = rng.intBetween(SPAWN.LINE_MIN, lineMax);
                if (usedLines.every((used) => Math.abs(candidate - used) >= WORLD.MIN_ENTITY_GAP_LINES)) {
                    line = candidate;
                    break;
                }
            }
            if (line < 0)
                continue;
            usedLines.push(line);
            const dir = rng.chance(1, 2) ? 1 : -1;
            const startX = dir === 1 ? -ENTITY.JET.OFFSCREEN_PX : SCREEN.WIDTH + ENTITY.JET.OFFSCREEN_PX;
            spawns.push({
                slot: spawns.length,
                type: 'jet',
                line: baseLine + line,
                xQ8: fp(startX),
                vxQ8: perTick(rng.intBetween(ENTITY.JET.SPEED_MIN, ENTITY.JET.SPEED_MAX)),
                dir,
            });
        }
    }
    return spawns;
}
// ─── Chunk ────────────────────────────────────────────────────────────────────
/**
 * Gera o chunk `index`. Pura: mesma entrada ⇒ mesmo chunk, em qualquer ordem
 * de chamada, em qualquer motor.
 *
 * `safe` transforma o chunk em RETA_SEGURA (§6.2): banda reta larga no meio,
 * marchas nas pontas, nenhuma entidade e nenhuma ilha — é a área de respawn.
 * Chunks de ponte (posicionais, `isBridgeChunk`) garantem largura ≥ 6 na
 * linha da ponte (regra 4 da §6.3) e não têm ilha.
 */
export function generateChunk(worldSeed, index, safe, playerCount = 1) {
    const section = sectionOfChunk(index);
    const leftTiles = new Array(WORLD.CHUNK_LINES);
    const rightTiles = new Array(WORLD.CHUNK_LINES);
    for (let i = 0; i < WORLD.CHUNK_LINES; i++) {
        const [left, right] = rasterizeTiles(naturalProfile(worldSeed, index * WORLD.CHUNK_LINES + i, playerCount));
        leftTiles[i] = left;
        rightTiles[i] = right;
    }
    const hasBridge = !safe && isBridgeChunk(index);
    if (safe) {
        forceBand(leftTiles, rightTiles, ...safeBandTiles([leftTiles[0], rightTiles[0]], [leftTiles[WORLD.CHUNK_LINES - 1], rightTiles[WORLD.CHUNK_LINES - 1]]), GEN.SAFE_BAND_START, GEN.SAFE_BAND_START + GEN.SAFE_BAND_LINES);
    }
    else if (hasBridge && rightTiles[BRIDGE.LINE] - leftTiles[BRIDGE.LINE] < BRIDGE.MIN_WIDTH_TILES) {
        // Regra 4 da §6.3: a linha da ponte precisa de largura ≥ 6. Banda curta
        // centrada no perfil natural, marchada como a reta segura.
        const naturalCenter = Math.round((leftTiles[BRIDGE.LINE] + rightTiles[BRIDGE.LINE]) / 2);
        const half = BRIDGE.MIN_WIDTH_TILES / 2;
        const lo = GEN.EDGE_MARGIN_TILES + half;
        const hi = SCREEN.GRID_W - GEN.EDGE_MARGIN_TILES - half;
        const center = clamp(naturalCenter, lo, hi);
        forceBand(leftTiles, rightTiles, center - half, center + half, BRIDGE.LINE - 1, BRIDGE.LINE + 2);
    }
    const leftPx = new Array(WORLD.CHUNK_LINES);
    const rightPx = new Array(WORLD.CHUNK_LINES);
    const islandLeftPx = new Array(WORLD.CHUNK_LINES).fill(0);
    const islandRightPx = new Array(WORLD.CHUNK_LINES).fill(0);
    for (let i = 0; i < WORLD.CHUNK_LINES; i++) {
        leftPx[i] = leftTiles[i] * SCREEN.TILE;
        rightPx[i] = rightTiles[i] * SCREEN.TILE;
    }
    // Ilha (§6.2): nunca em reta segura nem em chunk de ponte.
    if (!safe && !hasBridge) {
        const islandRng = new RngService(mixSeed(chunkSeed(worldSeed, index), SALT_ISLAND));
        const plan = planIsland(islandRng, leftTiles, rightTiles, section);
        if (plan !== null)
            rasterizeIsland(plan, islandLeftPx, islandRightPx);
    }
    const ctx = { leftPx, rightPx, islandLeftPx, islandRightPx };
    const spawns = safe ? [] : placeEntities(worldSeed, index, ctx, section, hasBridge, playerCount);
    return { index, safe, section, leftPx, rightPx, islandLeftPx, islandRightPx, spawns };
}
//# sourceMappingURL=river.js.map